<script lang="ts" setup>
// Renderless. MUST be inside <AisInstantSearch>. Bridges the IS helper
// lifecycle into useListingLoadingState to keep the skeleton up during reload.

const props = withDefaults(defineProps<{ label?: string }>(), { label: "" })

const { getInstance } = useInstantSearch()
const { markSearchStart, markSearchEnd } = useListingLoadingState()
const route = useRoute()

// 1-based page from the URL path (/page/N), matching IS's `uiState.page`
// convention (connectPagination stores uiState.page 1-based and converts to
// Algolia's 0-based `page` internally). Returns undefined for page 1.
const routeUiStatePage = () => {
  const fromParam = route.params?.page as string | undefined
  const fromPath = route.path.match(/\/page\/(\d+)$/)?.[1]
  const raw = fromParam ?? fromPath
  const n = raw ? parseInt(raw, 10) : 1
  return Number.isFinite(n) && n > 1 ? n : undefined
}

let detach: (() => void) | null = null

onMounted(() => {
  if (typeof window === "undefined") return

  let instance: any
  let helper: any
  try {
    instance = getInstance()
    helper = instance.value?.mainHelper
  } catch {
    return
  }
  if (!helper) return

  const onSearch = () => {
    markSearchStart()
  }
  const onSettled = () => {
    markSearchEnd()
  }

  // `search` fires when a request is sent; `searchQueueEmpty` fires when every
  // in-flight request has resolved (covers debounced/batched searches).
  helper.on("search", onSearch)
  helper.on("searchQueueEmpty", onSettled)
  helper.on("error", onSettled)

  // One-time correction AFTER widgets are ready: on the index.vue <-> page/[page]
  // remount the routing re-sync can be dropped (widgets briefly empty), leaving
  // the listing on page 1 while the URL says /page/N. We reconcile the page once
  // via setUiState (respecting the pagination widget → no controller conflict /
  // no loop). Subsequent page changes within page/[page] are handled by routing.
  nextTick(() => {
    try {
      const inst = instance.value
      const indexName = inst?.indexName
      const desired = routeUiStatePage()
      const current = inst?.getUiState?.()?.[indexName]?.page
      if (indexName && current !== desired) {
        inst.setUiState((prev: any) => ({
          ...prev,
          [indexName]: {
            ...(prev?.[indexName] ?? {}),
            page: desired
          }
        }))
      }
    } catch {
      /* instance not ready */
    }
  })

  detach = () => {
    helper.removeListener("search", onSearch)
    helper.removeListener("searchQueueEmpty", onSettled)
    helper.removeListener("error", onSettled)
  }
})

onBeforeUnmount(() => {
  detach?.()
  detach = null
})
</script>

<template>
  <span class="hidden" aria-hidden="true" />
</template>
